import { ReactElement, ReactNode, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ErrorState, formatDayMonth, GroupsSkeleton, ReportLoading, salesReportIcons } from '../../components/SalesReport'
import {
  AccessReportResponse,
  exportErrorMessage,
  useAccessReport,
  useAccessReportExport,
  useAccessReportFilters,
} from '../../hooks/access-report'
import { useAuth, useGuard, useHasAccess } from '../../hooks/auth'
import { useEvent } from '../../hooks/events'
import { SalesReportExportFormat } from '../../hooks/sales-report'
import { useDebounce } from '../../hooks/useDebounce'
import { useMediaQuery } from '../../hooks/useMediaQuery'
import { LOCAL_EXECUTION } from '../../utils/featureFlag'
import { AccessAndSalesSumaryPageComponent } from '../AcessAndSalesSumary'
import * as S from '../SalesReport/styles'
import { AccessReportFilterState, DEFAULT_FILTERS, periodError, toAccessReportRequest, withAvailableSelections } from './filters'
import { FiltersPanel } from './FiltersPanel'
import { ReportContent } from './ReportContent'

export const ACCESS_REPORT_PERMISSION = 'DP14'
export const NEW_ACCESS_REPORT_PERMISSION = 'DP27'

const SELECT_DEBOUNCE_MS = 400
const FORMAT_LABELS: Record<SalesReportExportFormat, string> = { csv: 'CSV', xlsx: 'XLSX' }

const reportErrorMessage = (error: any): string | undefined =>
  error?.response?.data?.errorCode === 'REPORT_TOO_LARGE' ? exportErrorMessage('REPORT_TOO_LARGE') : undefined

function IconPair({ desktop, mobile }: { desktop: string, mobile: string }): ReactElement {
  return (
    <>
      <S.DesktopOnly>
        <img src={desktop} alt="" width={16} height={16} />
      </S.DesktopOnly>
      <S.MobileOnly>
        <img src={mobile} alt="" width={16} height={16} />
      </S.MobileOnly>
    </>
  )
}

export function AccessReport(): ReactElement {
  useGuard([ACCESS_REPORT_PERMISSION])
  const { id } = useParams()
  const navigate = useNavigate()
  const { authHookWithEvent } = useAuth()
  const { event } = useEvent(Number(id))
  const { filters, isLoading, error, retry } = useAccessReportFilters(Number(id))
  const [filterState, setFilterState] = useState<AccessReportFilterState>(DEFAULT_FILTERS)
  const [period, setPeriod] = useState({ start: DEFAULT_FILTERS.periodStart, end: DEFAULT_FILTERS.periodEnd })
  const [live, setLive] = useState(false)
  const exporter = useAccessReportExport(Number(id))
  const mobile = useMediaQuery('(max-width: 767px)')
  const debouncedFilters = useDebounce(filterState, SELECT_DEBOUNCE_MS)

  const request = useMemo(() => {
    if (filters === undefined) return null
    const selected = withAvailableSelections(debouncedFilters, filters)
    const applied = { ...selected, periodStart: period.start, periodEnd: period.end }
    return periodError(applied) === null ? toAccessReportRequest(applied) : null
  }, [debouncedFilters, filters, period])
  const accessReport = useAccessReport(Number(id), request, live)
  const { report, error: reportError, refresh } = accessReport
  const reportLoading = accessReport.isLoading || (Boolean(reportError) && accessReport.isValidating)

  useEffect(() => {
    setLive(report?.event.status === 'live')
  }, [report?.event.status])

  useEffect(() => {
    if (!LOCAL_EXECUTION && id) authHookWithEvent(id, navigate)
  }, [id, authHookWithEvent, navigate])

  const exporting = exporter.state.status === 'running' ? exporter.state.format : null
  const canExport = request !== null && exporting === null
  const exportAs = (format: SalesReportExportFormat): void => {
    if (request !== null) void exporter.start(format, request)
  }
  const refreshing = accessReport.isValidating && !accessReport.isLoading
  const overline = event ? [formatDayMonth(event.data), event.nome].filter(Boolean).join(' — ') : ''

  let body: ReactNode = null
  if (request !== null) {
    if (reportLoading) body = <ReportLoading />
    else if (reportError) body = <ErrorState message={reportErrorMessage(reportError)} onRetry={refresh} />
    else if (report === undefined) body = <ReportLoading />
    else body = <ReportContent report={report as AccessReportResponse} mobile={mobile} />
  }

  return (
    <S.Page>
      <S.Header>
        <S.Heading>
          <S.Overline>{overline}</S.Overline>
          <S.Title>Relatório de Acessos</S.Title>
          <S.Description>Acompanhe as entradas do evento por horário, setor e dispositivo.</S.Description>
        </S.Heading>
        <S.Actions>
          <S.ActionButton
            type="button"
            disabled={request === null || refreshing}
            aria-busy={refreshing || undefined}
            onClick={refresh}
          >
            {refreshing ? 'Atualizando…' : '↻ Atualizar'}
          </S.ActionButton>
          <S.ActionButton
            type="button"
            disabled={!canExport}
            aria-label="Exportar CSV"
            aria-busy={exporting === 'csv' || undefined}
            onClick={() => exportAs('csv')}
          >
            <IconPair desktop={salesReportIcons.download} mobile={salesReportIcons.downloadMobile} />
            <S.DesktopOnly>Exportar CSV</S.DesktopOnly>
            <S.MobileOnly>CSV</S.MobileOnly>
          </S.ActionButton>
          <S.ActionButton
            type="button"
            $primary
            disabled={!canExport}
            aria-busy={exporting === 'xlsx' || undefined}
            onClick={() => exportAs('xlsx')}
          >
            <IconPair desktop={salesReportIcons.file} mobile={salesReportIcons.downloadLight} />
            Exportar XLSX
          </S.ActionButton>
        </S.Actions>
      </S.Header>
      <S.ExportStatus role="status" aria-live="polite">
        {exporting && `Gerando arquivo ${FORMAT_LABELS[exporting]}…`}
      </S.ExportStatus>
      {exporter.state.status === 'error' && <S.ExportError role="alert">{exporter.state.message}</S.ExportError>}
      {filters ? (
        <FiltersPanel
          options={filters}
          value={filterState}
          onChange={setFilterState}
          onPeriodCommit={(start, end) => setPeriod({ start, end })}
        />
      ) : error ? (
        <ErrorState message="Não foi possível carregar os filtros do relatório." onRetry={retry} />
      ) : isLoading ? (
        <GroupsSkeleton groups={1} label="Carregando filtros" />
      ) : null}
      {body}
    </S.Page>
  )
}

export function AccessSummaryRoute(): ReactElement {
  const { id } = useParams()
  const newReport = useHasAccess(NEW_ACCESS_REPORT_PERMISSION)
  if (newReport) return <AccessReport key={id} />
  return <AccessAndSalesSumaryPageComponent type={'access'} permissions={[ACCESS_REPORT_PERMISSION]} />
}
